import * as React from "react";
import LandingCard from "./landing_card";
import { Avatar, Button, ButtonBase, Skeleton, Stack, Typography, useTheme } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { REACT_QUERY_KEYS } from "../../../enums/react_query_key_enum";
import useBackend, { useLogin } from "../../../hooks/use_backend";
import { useAmplitudeTrack, useOpenSite, SITE_SELECTION_SOURCE } from "../../../scripts/amplitude_utils";
import { fetchFavouriteSites } from "../../../scripts/api_v2/site_client";
import { Star } from "@mui/icons-material";
import { TOOLTIPS_EN } from "../../../enums/tooltip_enum_EN";

function FavouriteSitesCard({ ...props }) {
  const login = useLogin();
  const backend = useBackend();

  const [count, setCount] = React.useState(5);
  const { trackButtonClicked } = useAmplitudeTrack();

  let { data: siteFavourites, isFetching: fetchingSiteFavourites } = useQuery({
    queryKey: [REACT_QUERY_KEYS.LANDING_PAGE_SITE_FAVOURITES, backend.fetch, login.company_name, count],
    queryFn: () => fetchFavouriteSites(backend.fetch, count),
    refetchInterval: 60000,
    placeholderData: (previousData, previousQuery) => previousData,
  });

  const list = siteFavourites?.list;
  const total = siteFavourites?.total;

  React.useEffect(() => {
    total && setCount(Math.min(total, count)); // count cannot be larger than data
  }, [total]);

  return (
    <LandingCard
      title="Favourite Sites"
      tooltip={TOOLTIPS_EN.FAVOURITE_SITES}
      {...props}
      sx={{
        display: "flex",
        flexDirection: "column",
        ...props.sx,
      }}
    >
      <Stack spacing={1}>
        {[...Array(count)].map((_, index) =>
          list && index < list.length ? (
            <FavouritesItem
              key={list[index].site_name}
              siteName={list[index].site_name}
              siteId={list[index].site_id}
              isLoading={false}
            />
          ) : (
            <FavouritesItem key={index} isLoading={true} isHidden={!fetchingSiteFavourites} />
          ),
        )}
      </Stack>

      {list?.length > 0 ? (
        <Button sx={{ marginTop: 1 }} onClick={() => {
          trackButtonClicked('favourite_sites_view_more', 'landing_page', { shown_count: count + 5 });
          setCount(count + 5);
        }}>
          View More
        </Button>
      ) : (
        <Button sx={{ marginTop: 1 }} disabled={true}>
          None found
        </Button>
      )}
    </LandingCard>
  );
}

export default FavouriteSitesCard;

const AVATAR_SIZE = 40;

function FavouritesItem({ siteId, siteName, isLoading, isHidden = false }) {
  const theme = useTheme();

  const openSite = useOpenSite();

  return !isHidden ? (
    <>
      <ButtonBase
        onClick={() => openSite(siteId, SITE_SELECTION_SOURCE.FAVOURITE_SITES_CARD)}
        sx={{
          width: "100%",
          alignItems: "center",
          display: "flex",
          textAlign: "left",
          borderRadius: "10px",
          padding: 1,
          "&:hover": { backgroundColor: "#f3f3f3" },
        }}
        disabled={isLoading}
      >
        {isLoading ? (
          <Skeleton variant="circular" width={AVATAR_SIZE} height={AVATAR_SIZE} />
        ) : (
          <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
            <Star />
          </Avatar>
        )}

        <Stack sx={{ flexGrow: 1, marginLeft: 2 }}>
          <Typography variant="body2">{isLoading ? <Skeleton /> : siteName}</Typography>
        </Stack>
      </ButtonBase>
    </>
  ) : null;
}
